// Before joining: pick a camera and mic, see yourself, watch the mic level,
// and get warned about the usual recording killers (Bluetooth headsets,
// blocked permissions) before anything is recorded.

import { useEffect, useRef, useState } from 'react';
import {
  bluetoothRisk,
  isIOS,
  constraintsFor,
  listDevices,
  pitchFor,
  stopStream,
  testSignal,
  type DeviceChoice,
  type Devices,
} from '../../lib/studio/media';
import { Button } from '../ui/Button';
import { SelectField } from '../ui/Field';
import { MicMeter } from './MicMeter';
import { VideoTile } from './VideoTile';

export interface DeviceCheckProps {
  name: string;
  /** Offer the built-in test signal (for checks without real devices). */
  allowTestSignal?: boolean;
  joinLabel?: string;
  onJoin: (stream: MediaStream, choice: DeviceChoice) => void;
  mediaDevices?: MediaDevices;
  makeTestSignal?: typeof testSignal;
}

export function permissionMessage(err: unknown): string {
  const name = (err as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Your browser blocked the camera and microphone. Allow them from the icon in the address bar, then try again.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No camera or microphone was found. Plug one in, or pick another.';
  if (name === 'NotReadableError') return 'Another app is using your camera or microphone. Close it and try again.';
  return 'Couldn’t start your camera and microphone.';
}

export function DeviceCheck({ name, allowTestSignal, joinLabel = 'Join', onJoin, mediaDevices, makeTestSignal = testSignal }: DeviceCheckProps) {
  const md = mediaDevices ?? navigator.mediaDevices;
  const [choice, setChoice] = useState<DeviceChoice>({ headphones: false, video: true });
  const [useTest, setUseTest] = useState(!!allowTestSignal);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [devices, setDevices] = useState<Devices>({ mics: [], cams: [] });
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const handedOff = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let stop = () => undefined as void;
    setError(null);
    (async () => {
      try {
        let s: MediaStream;
        if (useTest) {
          const signal = makeTestSignal({ name, hz: pitchFor(name), serverNow: () => Date.now() });
          s = signal.stream;
          stop = signal.stop;
        } else {
          try {
            s = await md.getUserMedia(constraintsFor(choice));
          } catch (err) {
            // No camera: carry on with the mic alone.
            if (!choice.video || (err as { name?: string }).name !== 'NotFoundError') throw err;
            s = await md.getUserMedia(constraintsFor({ ...choice, video: false }));
          }
          stop = () => stopStream(s);
          setDevices(await listDevices(md));
        }
        if (cancelled) return stop();
        setStream(s);
      } catch (err) {
        if (!cancelled) setError(permissionMessage(err));
      }
    })();
    return () => {
      cancelled = true;
      if (!handedOff.current) stop();
    };
  }, [md, choice, useTest, name, attempt, makeTestSignal]);

  const audio = stream?.getAudioTracks()[0];
  const micLabel = audio?.label ?? '';
  const rate = audio?.getSettings?.().sampleRate;
  const risky = !useTest && !!audio && bluetoothRisk(micLabel, rate);

  return (
    <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-[minmax(0,1fr)_18rem]">
      <div className="space-y-3">
        <VideoTile stream={stream} name={name} self mic cam={!!stream?.getVideoTracks().length} />
        <MicMeter stream={stream} />
      </div>
      <div className="space-y-4">
        {error && (
          <div role="alert" className="space-y-2 rounded-lg bg-danger-soft p-3 text-sm text-danger">
            <p>{error}</p>
            <Button size="sm" variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
          </div>
        )}
        {!useTest && (
          <>
            <SelectField
              label="Microphone"
              value={choice.micId ?? ''}
              onChange={(e) => setChoice((c) => ({ ...c, micId: e.target.value || undefined }))}
              options={[{ value: '', label: 'Default' }, ...devices.mics.map((d, i) => ({ value: d.deviceId, label: d.label || `Microphone ${i + 1}` }))]}
            />
            <SelectField
              label="Camera"
              value={choice.video ? (choice.camId ?? '') : 'off'}
              onChange={(e) =>
                setChoice((c) => (e.target.value === 'off' ? { ...c, video: false } : { ...c, video: true, camId: e.target.value || undefined }))
              }
              options={[
                { value: '', label: 'Default' },
                ...devices.cams.map((d, i) => ({ value: d.deviceId, label: d.label || `Camera ${i + 1}` })),
                { value: 'off', label: 'No camera' },
              ]}
            />
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={choice.headphones}
                onChange={(e) => setChoice((c) => ({ ...c, headphones: e.target.checked }))}
              />
              <span>
                I’m wearing headphones
                <span className="block text-xs text-muted">Turns off echo cancelling and noise suppression for a cleaner, natural voice.</span>
              </span>
            </label>
          </>
        )}
        {risky && (
          <p role="status" className="rounded-lg bg-warning-soft p-3 text-sm text-warning">
            That looks like a Bluetooth headset. Their mics drop to phone quality while recording — use your computer’s or a wired mic if you can.
          </p>
        )}
        {isIOS() && (
          <p role="note" className="rounded-lg bg-warning-soft p-3 text-sm text-warning">
            On iPhone and iPad, keep this page open and in front while recording — switching apps or locking the screen pauses your recording.
          </p>
        )}
        {allowTestSignal && (
          <label className="flex items-center gap-2 text-xs text-muted">
            <input type="checkbox" checked={useTest} onChange={(e) => setUseTest(e.target.checked)} />
            Use a test signal instead of a camera and mic
          </label>
        )}
        <Button
          className="w-full"
          disabled={!stream}
          onClick={() => {
            if (!stream) return;
            handedOff.current = true;
            onJoin(stream, choice);
          }}
        >
          {joinLabel}
        </Button>
      </div>
    </div>
  );
}
